/**
 * TanStack Query hooks over the UmagTennis REST API.
 * Pages should consume these hooks rather than refetching raw endpoints.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, STATIC_MODE } from './client';
import type {
  AdminPlayer,
  AdminVotesPayload,
  BucketEntry,
  Competitor,
  ComparisonInput,
  EditComparisonInput,
  Group,
  ImageData,
  LeaderboardRow,
  Match,
  Matchup,
  MatchSet,
  MatchType,
  Player,
  Rater,
  StandingsGroup,
  SurveyProgress,
  WeatherData,
  HourlyWeather,
} from './types';

export const queryKeys = {
  matches: ['matches'] as const,
  competitors: ['competitors'] as const,
  groups: ['groups'] as const,
  images: ['images'] as const,
  standings: ['standings'] as const,
  weather: (at?: string) => ['weather', at ?? 'now'] as const,
  forecast: (days: number) => ['weather', 'forecast', days] as const,
  players: ['players'] as const,
  rater: (token: string) => ['survey', 'rater', token] as const,
  buckets: (token: string) => ['survey', 'buckets', token] as const,
  next: (token: string, count: number) =>
    ['survey', 'next', token, count] as const,
  progress: (token: string) => ['survey', 'progress', token] as const,
  leaderboard: ['survey', 'leaderboard'] as const,
  adminVotes: ['admin', 'votes'] as const,
  adminPlayers: ['admin', 'players'] as const,
};

export function useMatches() {
  return useQuery({
    queryKey: queryKeys.matches,
    // STATIC MODE: /napok's only data dependency. Served from a bundled JSON
    // snapshot (public/data/matches.json) so it works with the backend off.
    // Reverse by unsetting VITE_STATIC (see STATIC_MODE in ./client.ts).
    queryFn: () =>
      STATIC_MODE
        ? fetch('/data/matches.json').then((r) => r.json() as Promise<Match[]>)
        : apiClient.get<Match[]>('/api/matches'),
  });
}

export function useCompetitors() {
  return useQuery({
    queryKey: queryKeys.competitors,
    queryFn: () => apiClient.get<Competitor[]>('/api/competitors'),
  });
}

export function useGroups() {
  return useQuery({
    queryKey: queryKeys.groups,
    queryFn: () => apiClient.get<Group[]>('/api/groups'),
  });
}

export function useImages() {
  return useQuery({
    queryKey: queryKeys.images,
    queryFn: () => apiClient.get<ImageData[]>('/api/images'),
  });
}

export function useStandings() {
  return useQuery({
    queryKey: queryKeys.standings,
    queryFn: () => apiClient.get<StandingsGroup[]>('/api/standings'),
  });
}

export function useWeather(at?: string) {
  return useQuery({
    queryKey: queryKeys.weather(at),
    // STATIC MODE: no weather backend during hibernation. Disable the query so
    // the match cards simply render without a forecast (they already handle
    // undefined weather gracefully). Reverse by unsetting VITE_STATIC.
    enabled: !STATIC_MODE,
    queryFn: () => {
      const qs = at ? `?at=${encodeURIComponent(at)}` : '';
      return apiClient.get<WeatherData>(`/api/weather${qs}`);
    },
  });
}

/** Hour-by-hour forecast (today + `days-1` days) for the weather page. */
export function useForecast(days = 3) {
  return useQuery({
    queryKey: queryKeys.forecast(days),
    queryFn: () =>
      apiClient.get<HourlyWeather[]>(`/api/weather/forecast?days=${days}`),
  });
}

/* ------------------------------------------------------------------ *
 * Mutations
 * ------------------------------------------------------------------ */

/** Standings depend on competitors + matches, so most mutations refresh it too. */

export interface CreateCompetitorInput {
  name: string;
  firstGroupName?: string | null;
  secondGroupName?: string | null;
  rankingInGroup: number;
}

export function useCreateCompetitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompetitorInput) =>
      apiClient.post<Competitor>('/api/competitors', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.competitors });
      queryClient.invalidateQueries({ queryKey: queryKeys.standings });
    },
  });
}

export function useDeleteCompetitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/api/competitors/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.competitors });
      queryClient.invalidateQueries({ queryKey: queryKeys.standings });
    },
  });
}

export interface UpdateCompetitorGroupsInput {
  id: string;
  firstGroupName: string | null;
  secondGroupName: string | null;
}

export function useUpdateCompetitorGroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateCompetitorGroupsInput) =>
      apiClient.put<Competitor>(`/api/competitors/${id}/groups`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.competitors });
      queryClient.invalidateQueries({ queryKey: queryKeys.standings });
    },
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiClient.post<Group>('/api/groups', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiClient.delete<void>(`/api/groups/${encodeURIComponent(name)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });
}

export interface CreateMatchInput {
  matchType: MatchType;
  groupName?: string | null;
  team1: string;
  team2: string;
  sets: MatchSet[];
  start: string;
  end: string;
  courtNumber: number;
}

export function useCreateMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMatchInput) =>
      apiClient.post<Match>('/api/matches', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches });
      queryClient.invalidateQueries({ queryKey: queryKeys.standings });
    },
  });
}

export interface UpdateMatchInput extends CreateMatchInput {
  id: string;
}

export function useUpdateMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateMatchInput) =>
      apiClient.put<Match>(`/api/matches/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches });
      queryClient.invalidateQueries({ queryKey: queryKeys.standings });
    },
  });
}

export function useDeleteMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/api/matches/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches });
      queryClient.invalidateQueries({ queryKey: queryKeys.standings });
    },
  });
}

export function useCreateImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cdnUrl: string) =>
      apiClient.post<ImageData>('/api/images', { cdnUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.images });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Player-rating survey (Phase 2)
 * ------------------------------------------------------------------ */

/** All survey players, used for the bucketing screen. */
export function usePlayers() {
  return useQuery({
    queryKey: queryKeys.players,
    queryFn: () => apiClient.get<Player[]>('/api/players'),
  });
}

/** Resolve a rater from their persisted magic-link token (404 if unknown). */
export function useRater(token: string | null) {
  return useQuery({
    queryKey: queryKeys.rater(token ?? ''),
    queryFn: () => apiClient.get<Rater>(`/api/survey/raters/${token}`),
    enabled: !!token,
    retry: false,
  });
}

/** Create-or-resume a rater from their per-person code; returns the full rater. */
export function useCreateRater() {
  return useMutation({
    mutationFn: (code: string) =>
      apiClient.post<Rater>('/api/survey/raters', { code }),
  });
}

/** The rater's knowledge buckets (every non-deleted player). */
export function useBuckets(token: string | null) {
  return useQuery({
    queryKey: queryKeys.buckets(token ?? ''),
    queryFn: () => apiClient.get<BucketEntry[]>(`/api/survey/${token}/buckets`),
    enabled: !!token,
  });
}

/** Save the full bucket map. Refreshes next/progress since eligibility changes. */
export function useSaveBuckets(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entries: Array<{ playerId: string; level: BucketEntry['level'] }>) =>
      apiClient.put<void>(`/api/survey/${token}/buckets`, { entries }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.buckets(token) });
      queryClient.invalidateQueries({ queryKey: ['survey', 'next', token] });
      queryClient.invalidateQueries({ queryKey: queryKeys.progress(token) });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard });
    },
  });
}

/** A batch of upcoming matchups (diversity-aware queue). */
export function useNextMatchups(token: string | null, count = 10) {
  return useQuery({
    queryKey: queryKeys.next(token ?? '', count),
    queryFn: () =>
      apiClient.get<Matchup[]>(`/api/survey/${token}/next?count=${count}`),
    enabled: !!token,
    staleTime: 0,
  });
}

/**
 * Submit a single comparison. The backend enforces a 5s throttle and replies 429;
 * callers should catch ApiError (status 429) to surface the remaining wait.
 */
export function useSubmitComparison(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ComparisonInput) =>
      apiClient.post<void>(`/api/survey/${token}/comparisons`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.progress(token) });
      queryClient.invalidateQueries({ queryKey: ['survey', 'next', token] });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard });
    },
  });
}

/**
 * Edit the rater's existing vote for a matchup (overwrite in place). An
 * `outcomeForA` of null removes the vote ("Nem tudom" / un-vote). Not throttled.
 */
export function useEditComparison(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EditComparisonInput) =>
      apiClient.put<void>(`/api/survey/${token}/comparisons`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.progress(token) });
      queryClient.invalidateQueries({ queryKey: ['survey', 'next', token] });
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard });
    },
  });
}

/** The rater's live progress (answered / coverage / tier). */
export function useSurveyProgress(token: string | null) {
  return useQuery({
    queryKey: queryKeys.progress(token ?? ''),
    queryFn: () =>
      apiClient.get<SurveyProgress>(`/api/survey/${token}/progress`),
    enabled: !!token,
  });
}

/** Ranked contributors leaderboard. */
export function useLeaderboard() {
  return useQuery({
    queryKey: queryKeys.leaderboard,
    queryFn: () =>
      apiClient.get<LeaderboardRow[]>('/api/survey/leaderboard'),
  });
}

/* ------------------------------------------------------------------ *
 * Admin vote-quality (X-Admin-Key gated)
 * ------------------------------------------------------------------ */

const adminHeaders = (adminKey: string) => ({ 'X-Admin-Key': adminKey });

/** Every vote + rater names + players, for the admin pruning panel. */
export function useAdminVotes(adminKey: string | null) {
  return useQuery({
    queryKey: queryKeys.adminVotes,
    queryFn: () =>
      apiClient.get<AdminVotesPayload>('/api/admin/votes', {
        headers: adminHeaders(adminKey!),
      }),
    enabled: !!adminKey,
    retry: false,
  });
}

/** Toggle one vote's quality-prune exclusion (reversible). */
export function useSetVoteExclusion(adminKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ voteId, isExcluded }: { voteId: string; isExcluded: boolean }) =>
      apiClient.put<void>(
        `/api/admin/votes/${voteId}/exclusion`,
        { isExcluded },
        { headers: adminHeaders(adminKey!) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminVotes });
    },
  });
}

/** Bulk-toggle every vote of one rater. */
export function useSetRaterExclusion(adminKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ raterId, isExcluded }: { raterId: string; isExcluded: boolean }) =>
      apiClient.put<{ updated: number }>(
        `/api/admin/raters/${raterId}/exclusion`,
        { isExcluded },
        { headers: adminHeaders(adminKey!) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminVotes });
    },
  });
}

/** Every player with their roster (active/inactive) flag, for Játékosmenedzsment. */
export function useAdminPlayers(adminKey: string | null) {
  return useQuery({
    queryKey: queryKeys.adminPlayers,
    queryFn: () =>
      apiClient.get<AdminPlayer[]>('/api/admin/players', {
        headers: adminHeaders(adminKey!),
      }),
    enabled: !!adminKey,
    retry: false,
  });
}

/** Toggle one player's roster membership (active/inactive). */
export function useSetPlayerActive(adminKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, isActive }: { playerId: string; isActive: boolean }) =>
      apiClient.put<void>(
        `/api/admin/players/${playerId}/active`,
        { isActive },
        { headers: adminHeaders(adminKey!) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminPlayers });
    },
  });
}
